// App-level notification bell + inbox sheet (W36.1, notification spec §10).
//
// component-api.md §10 names the header bell as the sanctioned replacement for
// a FAB, so a bell here is not a Material-pattern violation — it is the
// documented affordance for exactly this job.
//
// **Why this component lives in AppShell and not in the dashboard.** W36.0
// shipped a bell inside the dashboard route (`routes/dashboard/components/
// NotificationBell.tsx`) with tests asserting its `dashboard-bell` testid. That
// component was never rendered by any page, and three dashboard tests were
// failing against it at the start of W36.1. Worse, `MobileHeader` and `TopBar`
// each pointed their own bell at `/settings/notifications` — a route that does
// not exist. That left the product with a dead global entry *and* a dead
// dashboard-scoped one. See ADR-0060: the bell is app chrome, so it lives in
// the shell that every route already renders, and there is exactly one of it.
//
// In-app only (spec §2.7 R5.1). Nothing here registers a device token or opens
// a push channel; the badge is only as fresh as the inbox query.
//
// Two display rules that are spec, not taste (§10.2 / §2.3 N12):
//   - the badge hides at zero rather than rendering a grey "0";
//   - the count caps at "9+" so the pill cannot grow into the title bar.

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from "@/hooks/useDashboard";
import { useSheetVariant } from "@/lib/hooks/useSheetVariant";
import { NOTIFICATION_I18N, copyFor } from "@/lib/i18n/notifications";
import { cn } from "@/lib/utils/cn";
import { formatFeedTime } from "@/lib/utils/date";

export interface NotificationBellProps {
  /** Controlled from the shell so the button and sheet share one state. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationBell({ open, onOpenChange }: NotificationBellProps) {
  const { variant } = useSheetVariant();
  // Always on: the badge is the reason the bell exists, and gating it on the
  // sheet being open would make the badge flicker when the sheet closes.
  const { data } = useNotifications(true);
  const unreadCount = data?.unread_count ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-label={NOTIFICATION_I18N.openBell}
        data-testid="app-bell"
        // W42 F36: 38px of visual box, 44px of touch target via the `::after` on
        // `.shell-btn`. The previous `h-11 w-11 tap-target` set the *element* to
        // 44px, which is the axis conflation F36 removes — the bell was the only
        // 44px object on a wordmark row of 38px controls, so it read as a
        // different class of thing. Asserted on the class, not a rect: jsdom
        // applies no layout, so `getBoundingClientRect` would read 0.
        className={cn(
          "press shell-btn",
          "text-ink-2 transition-colors duration-quick ease-paper hover:bg-black/5",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
        )}
      >
        <Bell className="icon-lg" aria-hidden />
        {unreadCount > 0 ? (
          <span
            data-testid="app-bell-badge"
            className={cn(
              "absolute -right-0.5 -top-0.5 flex h-s-4 min-w-s-4 items-center justify-center",
              "rounded-pill bg-partner-strong px-1 text-[10px] font-bold text-paper",
              "tabular-nums",
            )}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>
      <NotificationSheet open={open} onOpenChange={onOpenChange} variant={variant} />
    </>
  );
}

function NotificationSheet({
  open,
  onOpenChange,
  variant,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: "mobile" | "desktop-modal" | "desktop-side";
}) {
  // Only fetch when the sheet is actually open — the first paint must not wait
  // on a second request (spec §5.1).
  const { data, isLoading } = useNotifications(open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const navigate = useNavigate();

  // N17: a kind this build does not know is skipped, not rendered with its raw
  // name. Filtering here (rather than per row) keeps the "is the list empty"
  // answer honest — a list of only-unknown kinds shows the empty state.
  const items = (data?.items ?? []).filter((item) => copyFor(item) !== null);
  const unreadCount = data?.unread_count ?? 0;

  const openDetail = useCallback(
    (href: string) => {
      onOpenChange(false);
      navigate(href);
    },
    [navigate, onOpenChange],
  );

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant={variant}
      title={NOTIFICATION_I18N.title}
      data-testid="notification-sheet"
    >
      {isLoading ? (
        <p className="text-body-sm text-ink-3">載入中…</p>
      ) : items.length === 0 ? (
        <p className="text-body-sm text-ink-3" data-testid="notification-empty">
          {NOTIFICATION_I18N.empty}
        </p>
      ) : (
        <>
          {/* One tap to clear the badge (ADR-0069). Hidden at zero rather than
              disabled: a disabled control that looks interactive is the W35.7
              "inert CTA" class, and the condition is already visible in the
              badge itself. */}
          {unreadCount > 0 ? (
            <div className="mb-s-2 flex justify-end">
              <button
                type="button"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                data-testid="notification-read-all"
                className={cn(
                  "press min-h-11 min-w-s-9 rounded-md px-s-3 text-meta font-semibold",
                  "text-you-strong hover:bg-black/5",
                  "disabled:opacity-50",
                )}
              >
                {markAll.isPending
                  ? NOTIFICATION_I18N.markAllPending
                  : NOTIFICATION_I18N.markAll}
              </button>
            </div>
          ) : null}
          <ul className="flex flex-col divide-y divide-hairline" data-testid="notification-list">
            {items.map((item) => {
              // Filtered above, so this lookup cannot be null — but the compiler
              // does not know that, and a `!` here would be the wrong way to say so.
              const copy = copyFor(item);
              if (copy === null) return null;
              const href = copy.href(item.payload ?? {});
              // When it happened. `created_at` has been on NotificationRead and in
              // every response this sheet has ever rendered, but the row showed
              // only 「未讀」 — so four identical "TA 留言在你的日記下面" lines sat
              // in the list with nothing to tell them apart and no way to judge
              // whether the interaction still mattered.
              const when = formatFeedTime(item.created_at);
              return (
              <li
                key={item.id}
                data-testid={`notification-${item.id}`}
                data-kind={item.kind}
                data-unread={item.read_at ? "false" : "true"}
                className="flex flex-col gap-s-1 py-s-3"
              >
                <p className="text-body-sm text-ink-1">{copy.title(item.payload ?? {})}</p>
                <div className="flex items-center justify-between gap-s-2">
                  <span className="flex items-center gap-s-1 text-meta text-ink-3 tabular-nums">
                    {item.read_at ? null : NOTIFICATION_I18N.unread}
                    {when ? (
                      <>
                        {item.read_at ? null : <span aria-hidden>·</span>}
                        <time dateTime={item.created_at ?? undefined}>{when}</time>
                      </>
                    ) : null}
                  </span>
                  <div className="flex items-center gap-s-2">
                    {item.read_at ? null : (
                      <button
                        type="button"
                        onClick={() => markRead.mutate(item.id)}
                        aria-label={NOTIFICATION_I18N.markRead}
                        data-testid={`notification-mark-read-${item.id}`}
                        className="press min-h-11 min-w-s-9 rounded-md px-s-2 text-meta font-semibold text-you-strong hover:bg-black/5"
                      >
                        {NOTIFICATION_I18N.markRead}
                      </button>
                    )}
                    {href ? (
                      <button
                        type="button"
                        onClick={() => openDetail(href)}
                        data-testid={`notification-open-${item.id}`}
                        className="press min-h-11 min-w-s-9 rounded-md px-s-2 text-meta font-semibold text-you-strong hover:bg-black/5"
                      >
                        {NOTIFICATION_I18N.open}
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
              );
            })}
          </ul>
        </>
      )}
    </BottomSheet>
  );
}
